/**
 * HomeTodosCard.jsx: "My to-dos" at the top of Home. The viewer's own overdue
 * and due-today to-dos (GET /todos/summary items, in the order the viewer
 * dragged their list into, as the server sends them), whoever's plate an admin
 * is looking at. Tick to finish; the $ next to the tick marks it (gold) or clears
 * it; a row opens it on /todos. No grips here.
 *
 * A failed load says so and offers Try again: it must never read as "nothing due".
 * Titles and links are to-do and record text, so every control has a static
 * data-track (home.todos.*).
 */
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Link, Checkbox, Button, Skeleton } from '@mui/material';
import { useTodoSummary, useUpdateTodo, useSetTodoMoney } from '../../hooks/useTodos';
import { useToast } from '../layout/Toast';
import { fmtDue, dueTone } from '../../lib/todoText';
import TodoLinkChip from '../todos/TodoLinkChip';
import MoneyMark from '../todos/MoneyMark';

const monoSx = { fontFamily: '"DM Mono", monospace' };

export default function HomeTodosCard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data, isPending, isError, refetch } = useTodoSummary();
  const update = useUpdateTodo();
  const setMoney = useSetTodoMoney();
  const today = data?.today;
  const items = (data?.items || []).filter((t) => !t.done);
  const overdue = data?.overdueCount || 0;

  // mutateAsync: each tick reports its own error, even when two overlap.
  const finish = (t) => update.mutateAsync({ id: t.id, done: true })
    .catch((err) => toast(err?.message || 'Could not save it. Try again.', 'error'));
  const toggleMoney = (t) => setMoney.mutateAsync({ id: t.id, money: !t.money })
    .catch(() => toast('Could not save it. Try again.', 'error'));

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="overline" color="text.secondary">
          My to-dos{overdue > 0 && (
            <Typography component="span" variant="overline" sx={{ color: 'error.main', ml: 1 }}>{overdue} overdue</Typography>
          )}
        </Typography>
        <Link
          data-track="home.todos.all"
          component={RouterLink}
          to="/todos"
          underline="hover"
          variant="caption"
          sx={{ display: 'inline-flex', alignItems: 'center', minHeight: { xs: 44, sm: 0 } }}
        >
          All to-dos
        </Link>
      </Box>
      {isPending ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Skeleton variant="rounded" height={28} />
          <Skeleton variant="rounded" height={28} />
        </Box>
      ) : isError && !data ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="error" sx={{ ...monoSx, fontSize: '0.75rem' }}>Could not load to-dos.</Typography>
          <Button data-track="home.todos.retry" size="small" onClick={() => refetch()} sx={{ textTransform: 'none', minHeight: 44 }}>Try again</Button>
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem' }}>Nothing due today.</Typography>
      ) : (
        items.map((t) => {
          const tone = dueTone(t.dueDate, today);
          return (
            <Box
              key={t.id}
              data-track="home.todos.row"
              onClick={() => navigate(`/todos?todo=${encodeURIComponent(t.id)}`)}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: { xs: 0.5, md: 0.25 }, px: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Checkbox
                data-track="home.todos.done"
                checked={false}
                size="small"
                onClick={(e) => e.stopPropagation()}
                onChange={() => finish(t)}
                slotProps={{ input: { 'aria-label': 'Mark done' } }}
                sx={{ p: { xs: 1.5, sm: 0.5 } }}
              />
              <MoneyMark
                dense
                marked={!!t.money}
                onToggle={() => toggleMoney(t)}
                trackKey="home.todos.money"
                sx={{ ml: { xs: -0.5, sm: -0.25 } }}
              />
              <Box sx={{ flex: 1, minWidth: 0, pt: { xs: 1.5, sm: 0.5 } }}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500, overflowWrap: 'anywhere' }}>{t.title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ ...monoSx, fontSize: '0.7rem', ...(tone === 'overdue' ? { color: 'error.main', fontWeight: 600 } : { color: 'text.primary', fontWeight: 700 }) }}
                  >
                    {fmtDue(t.dueDate, today)}
                  </Typography>
                  {t.link && <TodoLinkChip link={t.link} trackKey="home.todos.link" />}
                </Box>
              </Box>
            </Box>
          );
        })
      )}
    </Paper>
  );
}
